import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Container, Form } from './styles/StyledGeneralData';

import { Input } from '~/presentation/components/UI';
import InputKeyboardDate from '~/presentation/components/inputKeyboardDate';
import { translator } from '~/presentation/components/i18n';

import { iListConsultant, iValidation } from './interface';

import { iProfessional } from '~/domain/interfaces/models/Professional';
import { Navigator } from '../register/Navigator';
import { schemaGeneralData } from '~/validation/validators/document/CreateClinicalDocValidator';
import SearchSelect from '../UI/searchSelect';
import _ from 'lodash';

export interface ownProps {
  consultant: iListConsultant[];
  professional: iProfessional;
  next: (data: iValidation) => any;
  state?: iValidation;
  disabled?: boolean;
}

interface iStateParams {
  professional: string;
  consultant: string;
}

const GeneralData: React.FC<ownProps> = ({
  professional,
  next,
  state,
  disabled,
}): JSX.Element => {
  const stateParams = useLocation<iStateParams>().state;

  const { errors, handleSubmit, register, setValue, watch } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(schemaGeneralData),
    defaultValues: {
      ...state,
      orgUnit: 1,
      org: 1,
    },
  });

  register('orgUnit');

  const onSubmit = handleSubmit(data => {
    next({ ...data });
  });

  const generateProfessionalSelectOptions = useCallback(() => {
    return _.sortBy(
      professional?.results.flatMap(value => {
        return [
          {
            value: value.professional.id,
            label: `${value.user.firstName} ${value.user.lastName}`,
          },
        ];
      }),
      item => item?.label.replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase(),
    );
  }, [professional]);

  const professionalMultiselectSelected = useMemo(() => {
    return generateProfessionalSelectOptions().find(
      option => option.value === watch('professional'),
    );
  }, [generateProfessionalSelectOptions, watch]);

  return (
    <Container>
      <Form onSubmit={onSubmit}>
        <Input
          name="org"
          label={`${translator('Instituição')}`}
          onChange={e => setValue('org', Number(e.target.value))}
          register={() => register('org')}
          error={Boolean(errors.org)}
          message={errors.org?.message}
          placeholder={state?.orgUnitName}
          disabled={disabled || (state && state.orgUnitName !== '')}
          autoFocus
          required
        />

        <SearchSelect
          name="professional"
          register={() => register('professional')}
          label={`${translator('Profissional')}`}
          placeholder={translator('Selecione um profissional')}
          onChange={e => setValue('professional', Number(e?.value))}
          error={Boolean(errors.professional)}
          message={
            errors.professional?.message
              ? translator(errors.professional?.message)
              : ''
          }
          isDisabled={
            disabled || (stateParams && Number(stateParams.professional) !== -1)
          }
          required
          options={generateProfessionalSelectOptions()}
          value={professionalMultiselectSelected}
        />

        <InputKeyboardDate
          state={new Date()}
          setState={() => ''}
          label={`${translator('Data de emissão')}`}
          placeholder="DD/MM/AAAA"
          disabled
        />
        <Navigator />
      </Form>
    </Container>
  );
};

export default GeneralData;
